import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Stack,
  Text,
} from '@eluve/components';

import { useRegeneratePatientOverview } from '../../hooks/useRegeneratePatientOverview';

interface RegeneratePatientOverviewProps {
  disabled?: boolean;
  tenantId: string;
  appointmentId: string;
}

export const RegeneratePatientOverview: React.FC<
  RegeneratePatientOverviewProps
> = ({ disabled, tenantId, appointmentId }) => {
  const regeneratePatientOverview = useRegeneratePatientOverview();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>();

  const handleSubmit = async () => {
    setError(undefined);
    setProcessing(true);
    try {
      await regeneratePatientOverview(tenantId, appointmentId);
      setIsDialogOpen(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <Icon name="RotateCw" size="auto" />
          Regenerate Overview
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Regenerate Patient Overview</DialogTitle>
        </DialogHeader>
        <Stack gap={2}>
          <Text size="md">
            This will re-run the patient overview workflow with the tool-enabled
            agent (artifacts + FHIR context). Are you sure you want to continue?
          </Text>
          {error && (
            <Text size="md" className="text-text-negative">
              {error}
            </Text>
          )}
        </Stack>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={disabled ?? processing}
            loading={processing}
          >
            Regenerate Patient Overview
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
